import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Alert,
  FacingDirectionBadge,
  FacingConfirmationDialog,
  StepIndicator,
  TagInput,
} from '@/components/ui';
import { VideoUploader } from '@/components/video';
import { type Annotation } from '@/components/canvas';
import { PhaseDetectionPreview, ScoringStudio, TeachingVideoUploader, AnnotationSuggestionPanel, PhaseReconciliationPanel, FigureLabelStep, PerFigureAnnotationTabs, YoutubeTranscriptFallbackNotice } from '@/components/template';
import type { FigureSelection, FigureAnnotationState } from '@/components/template';
import { emptyFigureState } from '@/components/template';
import { PhaseEditor, VideoTrimTool } from '@/components/phase';
import type { PhaseConfig } from '@/components/phase';
import { useFacingDetection, useVideoMetadata, useTemplate } from '@/hooks';
import { useVideoPersons } from '@/hooks/useVideoPersons';
import { useMediaUrl } from '@/hooks/useMediaUrl';
import { createTemplate, updateTemplate } from '@/services/templateApi';
import type { FigureCreate } from '@/services/templateApi';
import { linkTeachingVideoToTemplate, importTeachingFromYoutube } from '@/services/teachingVideoApi';
import PersonPicker from '@/components/evaluation/PersonPicker';
import type { MaterializedAnnotation, Phase, VideoApiResponse, Video } from '@/types/api';
import type { FormEffectCurve } from '@/types/goalAuthoring';
import StrictnessSelector from '@/components/template/StrictnessSelector';
import type { StrictnessLevel } from '@/components/template/StrictnessSelector';
import { VIDEO_CONSTRAINTS, MOVEMENT_TYPES, SPORT_CATEGORIES, ATHLETE_LEVELS, COMMON_MISTAKES_BY_MOVEMENT_TYPE, GUIDE_DISMISSED_STUDIO_KEY } from '@/utils/constants';
import { useAuth } from '@/contexts/AuthContext';
import { isPaidCoach } from '@/utils/coachAccess';
import { cn } from '@/utils/cn';
import {
  appendMaterializedAnnotations,
  replaceMaterializedAnnotations,
} from '@/utils/materialization';
import { declaredSideForFocusAreas } from '@/utils/regionCardCopy';
import { turnNotesIntoChecks } from '@/utils/notesToChecks';
import { useAdaptiveGuide } from '@/contexts/AdaptiveGuideContext';
import { StudioGuideOverlay } from '@/components/overlay/StudioGuideOverlay';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import {
  FACING_CLEARED,
  facingCleared,
  facingFromCoach,
  facingFromDetector,
  facingFromDismissedDialog,
  facingFromStored,
  facingSelectorClick,
  type FacingState,
} from '@/utils/facingSource';

// Step definitions for StepIndicator (5 steps, phases + scoring merged into one)
const WIZARD_STEPS = [
  { label: 'Upload', description: 'Video & clip boundaries' },
  { label: 'Detect', description: 'Auto-detect phases' },
  { label: 'Phases', description: 'Define & configure' },
  { label: 'Score', description: 'Scoring studio' },
  { label: 'Save', description: 'Finalize' },
];

/**
 * B3 — the camera-angle vocabulary, as a closed set.
 *
 * The UI used to offer `front` and `behind`, neither of which the backend has
 * ever recognised (`template_validation.py` matches `{side, frontal,
 * diagonal}`), and it wrote the coach's free text into the same key as the
 * vocabulary value. The result: one finalized template carrying `front` in
 * both keys, a narrative gate that silently failed open, and a direction
 * resolver reading any non-"right" value as left.
 *
 * `frontal` is the spelling the backend reads. `behind` is dropped — a
 * movement filmed from behind resolves to 'other' plus free text, which is
 * what it always meant. Free text now lives in `camera_angle_other`.
 */
const CAMERA_ANGLE_OPTIONS = [
  { value: 'side', label: 'Side', desc: 'Perpendicular to movement' },
  { value: 'frontal', label: 'Front', desc: 'Facing the athlete' },
  { value: 'diagonal', label: 'Diagonal', desc: '45° angle' },
  { value: 'other', label: 'Other', desc: '' },
] as const;

const CAMERA_ANGLE_VALUES: string[] = CAMERA_ANGLE_OPTIONS.map((o) => o.value);

/**
 * Shown when the template saved but its coach instruction did not attach
 * (audit items `FE-4` / `FE-14`).
 *
 * WHAT: the template is saved, the recording is not attached. WHY: the
 * suggestions already accepted are safe, but nothing can be re-analysed from a
 * recording that is not linked. HOW: come back to this editor and add it again
 * — which is a real action now that the edit branch links (the previous
 * sentence sent the coach to the template details page, which has no attach
 * action; it only plays an already-linked video).
 */
const TEACHING_VIDEO_LINK_FAILED =
  'Your template is saved, but your coach instruction was not attached to it. ' +
  'The checks you already added are safe — the recording itself is not, so we ' +
  "can't look at it again later. Open this template in the editor and add your " +
  'coach instruction again.';

function CreateTemplate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { id: editTemplateId } = useParams<{ id?: string }>();
  const isEditMode = !!editTemplateId;
  const { user } = useAuth();

  // Adaptive Guide — B-2026-0058
  const { isGuideOpen, setGuideOpen } = useAdaptiveGuide();

  // Open the guide on first visit unless the coach has dismissed it before.
  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(GUIDE_DISMISSED_STUDIO_KEY);
      if (!dismissed) {
        setGuideOpen(true);
      }
    } catch {
      // localStorage unavailable — non-fatal; leave guide closed.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount — do NOT add setGuideOpen to deps (stable callback ref).

  const videoIdFromQuery = searchParams.get('videoId');
  const channelIdFromQuery = searchParams.get('channel_id') ?? undefined;
  const channelSlugFromQuery = searchParams.get('channel_slug') ?? undefined;

  // Template metadata
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateNotes, setTemplateNotes] = useState('');
  const [movementType, setMovementType] = useState('');
  const [sportCategory, setSportCategory] = useState<string>('');
  const [athleteLevel, setAthleteLevel] = useState<string>('');
  const [coachingCues, setCoachingCues] = useState<string[]>([]);
  const [commonMistakes, setCommonMistakes] = useState<string[]>([]);
  const [performanceNotes, setPerformanceNotes] = useState<string[]>([]);
  const [strictness, setStrictness] = useState<StrictnessLevel>('standard');
  const [cameraDevice, setCameraDevice] = useState<string>('smartphone');
  const [cameraDeviceOther, setCameraDeviceOther] = useState<string>('');
  const [cameraOrientation, setCameraOrientation] = useState<string>('landscape');
  const [cameraAngle, setCameraAngle] = useState<string>('side');
  const [cameraAngleOther, setCameraAngleOther] = useState<string>('');
  // Handedness is now auto-detected on the backend - default to 'ambidextrous' for all templates
  const [templateHandedness] = useState<'right' | 'left' | 'ambidextrous'>('ambidextrous');

  // Phase 2 declarative handedness — exercise symmetry + lead side
  const [exerciseSymmetry, setExerciseSymmetry] = useState<'symmetric' | 'asymmetric' | 'rotational' | ''>('');
  const [templateLeadSide, setTemplateLeadSide] = useState<'left' | 'right' | ''>('');

  // Positional team-drill mode (PTD-3).
  // Default is 'solo' — the existing wizard path is UNCHANGED when solo.
  const [templateKind, setTemplateKind] = useState<'solo' | 'positional'>('solo');

  // Per-figure state for positional mode (keyed by person_index)
  const [figureSelections, setFigureSelections] = useState<FigureSelection[]>([]);
  const [figureStates, setFigureStates] = useState<Record<number, FigureAnnotationState>>({});

  function handleFigureStateChange(personIndex: number, partial: Partial<FigureAnnotationState>) {
    setFigureStates((prev) => ({
      ...prev,
      [personIndex]: { ...(prev[personIndex] ?? emptyFigureState(trimStartFrame, trimEndFrame)), ...partial },
    }));
  }

  // Video state
  const [videoId, setVideoId] = useState<string | null>(videoIdFromQuery);

  // Person selection — null until resolved (auto or manual)
  const [focalPersonIndex, setFocalPersonIndex] = useState<number | null>(null);

  // Fetch detected persons for the uploaded video
  const { data: personsData, isLoading: isLoadingPersons } = useVideoPersons(videoId || undefined);

  // Fetch video metadata using the hook
  const { data: fetchedVideoMetadata, error: metadataError } = useVideoMetadata(videoId || undefined);

  // Media-auth security sprint (2026-09-21): /videos/{id}/stream now requires
  // a bearer token or a signed sig/exp pair — a bare <video src> sends
  // neither. streamUrl is null while the media-token fetch is in flight.
  const { streamUrl: videoStreamUrl } = useMediaUrl(videoId || undefined);

  // Local video metadata state (for when metadata is passed directly from upload)
  const [videoMetadata, setVideoMetadata] = useState<{
    width: number;
    height: number;
    fps: number;
    totalFrames: number;
  }>({ width: 640, height: 480, fps: VIDEO_CONSTRAINTS.DEFAULT_FPS, totalFrames: 0 });

  // Trim values — set by VideoTrimTool in Step 0.  Absolute raw-video frame numbers.
  // Phase boundaries remain relative to the trimmed clip (trim-relative frame space).
  const [trimStartFrame, setTrimStartFrame] = useState(0);
  const [trimEndFrame, setTrimEndFrame] = useState(0);
  // Annotations
  const [annotations, setAnnotations] = useState<Annotation[]>([]);

  // Phases
  const [detectedPhases, setDetectedPhases] = useState<Phase[]>([]);
  const [manualPhases, setManualPhases] = useState<Phase[]>([]);

  // Global body match settings
  const [globalBodyMatchEnabled, setGlobalBodyMatchEnabled] = useState(true);
  const [globalBodyMatchWeight, setGlobalBodyMatchWeight] = useState(0.5);
  const [rotationSensitive, setRotationSensitive] = useState(false);

  // Goal Score PR-7b — the coach-authored Form-effect curve
  // (metadata.form_effect_curve). Undefined = the template has never
  // touched the control; it resolves to Neutral at score time
  // (app/models/form_effect.py::resolve_form_effect_curve) and the Studio
  // prefills the percentile-derived default the first time it's opened
  // (FormEffectCurveEditor's own `value ?? DEFAULT_FORM_EFFECT_CURVE`).
  const [formEffectCurve, setFormEffectCurve] = useState<FormEffectCurve | undefined>(undefined);

  // Kinetic-chain sequencing coaching (opt-in, default off)
  const [enableSequencingCoaching, setEnableSequencingCoaching] = useState(false);

  // Economy of motion settings
  const [economyOfMotionWeight, setEconomyOfMotionWeight] = useState(0.10);

  // Phase scoring configuration
  const [phaseConfigs, setPhaseConfigs] = useState<PhaseConfig[]>([]);
  // Per-phase speed intent: phaseId -> new vocabulary (P1-3a, B-2026-0122).
  // Backend normalizes old+new vocab on read — not-yet-migrated templates are safe.
  const [phaseSpeedIntents, setPhaseSpeedIntents] = useState<Record<string, 'match' | 'reward_faster' | 'reward_slower' | 'off'>>({});
  // P1-1/P1-2 speed scoring master switch. Default 'off' (form-only, byte-identical).
  const [speedMode, setSpeedMode] = useState<'off' | 'on'>('off');
  // P1-4 speed weight: 0.10 / 0.15 / 0.25. Default 0.15.
  // PRESERVE: must match DEFAULT_SPEED_WEIGHT in ScoringStudio.tsx and the backend default.
  const [speedWeight, setSpeedWeight] = useState<number>(0.15);
  // P1-4 R7 safeguard: true = acknowledged (or no preview available).
  // Starts true so existing edit-mode templates with speed=on don't force re-ack on open.
  // Reset to false only when the coach actively changes speed settings in edit mode.
  const [speedAcknowledged, setSpeedAcknowledged] = useState<boolean>(true);
  // Mirror-invariant scoring is now always enabled (no UI toggle needed)

  // Teaching video (coach instruction extraction — optional step in Scoring Studio)
  const [teachingVideoId, setTeachingVideoId] = useState<string | null>(null);
  /**
   * FE-3 — the check the coach should be looking at right now.
   *
   * Set when exactly one check was just added, so Scoring Studio opens it with
   * its settings filled in instead of leaving the coach to find it. Cleared on
   * a bulk add and once the studio has opened it, so it is a one-shot request
   * and never a sticky "always open this" state.
   */
  const [openCheckpointId, setOpenCheckpointId] = useState<string | null>(null);
  // YTI: optional YouTube reference link + transcript-import state.
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [ytImportStatus, setYtImportStatus] = useState<
    { type: 'idle' | 'loading' | 'success' | 'error'; message?: string }
  >({ type: 'idle' });
  // PR-9: the step-4 Notes box can be routed into the same extraction a pasted
  // note takes (see utils/notesToChecks.ts). State only; `coach_notes` keeps
  // its narrative role and the text stays in the box.
  const [notesToChecksStatus, setNotesToChecksStatus] = useState<
    { type: 'idle' | 'loading' | 'success' | 'error'; message?: string }
  >({ type: 'idle' });
  /**
   * Append the checks the BACKEND materialiser built from accepted suggestions.
   *
   * This replaced two handlers that each ran a client-side converter
   * (`suggestedGoalToAnnotation` / `suggestedEmphasisToAnnotation`) plus local
   * phase reconciliation — all of which the backend also did, differently. The
   * client's version wrote field names `JointAngleConfig` does not have, so the
   * angle range a coach said out loud was dropped on every write (TV-3 / FE-1 /
   * E2E-3). Nothing is adjusted, defaulted or re-scoped here: these annotations
   * are already exactly what gets saved.
   *
   * Coach-facing notices (including the reason an item was NOT added) live in
   * `AnnotationSuggestionPanel`, next to the card they belong to — this page no
   * longer composes any of that text.
   */
  const handleAnnotationsMaterialized = useCallback(
    (materialized: MaterializedAnnotation[], replacesIds?: string[]) => {
      if (materialized.length === 0) return;
      // PR-7: `replacesIds` arrives when the coach switched which number a
      // check is judged against, so the server rebuilt a check this list
      // already holds. Appending it would leave two copies of one check, each
      // judged against a different number, both counting. Replacing keeps
      // every other annotation — and every box and edit the coach has made —
      // exactly where it is.
      setAnnotations((prev) =>
        replacesIds && replacesIds.length > 0
          ? replaceMaterializedAnnotations(prev, replacesIds, materialized)
          : appendMaterializedAnnotations(prev, materialized),
      );
      // FE-3 — accept now OPENS the check it just added, filled in.
      //
      // The old copy promised "Accept opens the goal config form pre-filled";
      // no form was ever opened, the check was appended silently, and the only
      // way to reach its settings was to know about the pencil icon in the
      // checkpoint list. One check added = open that one. A BULK add opens
      // nothing: twelve checks landed and singling one out would be an
      // arbitrary choice presented as a decision.
      setOpenCheckpointId(materialized.length === 1 ? materialized[0].id : null);
    },
    [],
  );

  const handleAddCueFromTeachingVideo = useCallback((cue: string) => {
    setCoachingCues((prev) => prev.includes(cue) ? prev : [...prev, cue]);
  }, []);

  const handleAddMistakeFromTeachingVideo = useCallback((mistake: string) => {
    setCommonMistakes((prev) => prev.includes(mistake) ? prev : [...prev, mistake]);
  }, []);

  // YTI-4: import a transcript from the YouTube link and route it into the
  // existing teaching-video flow (sets teachingVideoId so it links + processes
  // on save, exactly like an uploaded/pasted teaching video).
  const handleImportYoutubeTranscript = useCallback(async () => {
    if (!youtubeUrl.trim()) return;
    setYtImportStatus({ type: 'loading' });
    try {
      const res = await importTeachingFromYoutube(
        youtubeUrl.trim(),
        sportCategory,
        movementType,
      );
      setTeachingVideoId(res.id);
      setYtImportStatus({
        type: 'success',
        message: 'Transcript imported — it will seed coaching cues when you save.',
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detailMsg = (err as { response?: { data?: { detail?: { message?: string } } } })
        ?.response?.data?.detail?.message;
      setYtImportStatus({
        type: 'error',
        message:
          status === 503
            ? 'Automatic import is off on this server. Open the video on YouTube, click “Show transcript”, and paste it instead.'
            : detailMsg ?? 'Could not import the transcript. Try the paste option instead.',
      });
    }
  }, [youtubeUrl, sportCategory, movementType]);

  /**
   * "Turn this into checks" on the step-4 Notes box.
   *
   * Routes the typed notes through `turnNotesIntoChecks`, which posts the SAME
   * two calls the Coach Instruction card's Paste Notes tab posts, and then sets
   * `teachingVideoId` — the one join to the suggestion panel and from there to
   * the one materialiser. Nothing about the notes field itself changes: the
   * text stays in the box and is still saved as `coach_notes`.
   */
  const handleTurnNotesIntoChecks = useCallback(async () => {
    if (!templateNotes.trim()) return;
    setNotesToChecksStatus({ type: 'loading' });
    try {
      const id = await turnNotesIntoChecks({
        text: templateNotes,
        sport: sportCategory,
        movement: movementType || templateName,
      });
      setTeachingVideoId(id);
      setNotesToChecksStatus({
        type: 'success',
        message:
          'We are reading your notes now. Go back to Scoring Studio to see the ' +
          'checks we suggest and add the ones you want.',
      });
    } catch (err: unknown) {
      setNotesToChecksStatus({
        type: 'error',
        // The server writes its own coach-facing sentence for a refusal (for
        // example notes that are too long), so it is shown as-is.
        message:
          err instanceof Error && err.message
            ? err.message
            : 'We could not read your notes just now. Try again in a moment.',
      });
    }
  }, [movementType, sportCategory, templateName, templateNotes]);

  // Facing direction detection
  const facingDetection = useFacingDetection();
  const [showFacingConfirmDialog, setShowFacingConfirmDialog] = useState(false);
  /**
   * The performer's facing direction AND where that value came from, held as
   * ONE value so the Phase 1 contract's joint pin (`facing_source` is null if
   * and only if `facing_direction` is null) cannot be broken by a writer that
   * forgets its sibling — and there are seven writers below. Every transition
   * goes through a named function in @/utils/facingSource, which is where the
   * per-writer rules live and are tested.
   */
  const [facing, setFacing] = useState<FacingState>(FACING_CLEARED);
  const facingDirection = facing.direction;
  const [facingDismissed, setFacingDismissed] = useState(false);

  /**
   * Show the facing selector for every camera angle except Front. This is a
   * SUPERSET of the backend's requirement (which needs the field whenever the
   * effective view is not frontal/diagonal — i.e. side, other or unknown) and
   * of the old `side || diagonal` UI, so there is no state in which the save
   * demands a field the form has hidden.
   */
  const facingSelectorVisible = cameraAngle !== 'frontal';
  /**
   * Whether the coach may clear the facing direction. Only on diagonal, where
   * the backend does not require it (it is used there for the coaching
   * narrative's sense of "forward", not for near/far side resolution). On
   * side / other / not-yet-chosen the field is required at finalize, so the
   * clear affordances are withheld rather than offering a one-click route to
   * a template that cannot be published.
   */
  const facingClearable = cameraAngle === 'diagonal';

  // Current step (0-4: Upload, Auto-Detect Phases, Define Phases + Scoring, Annotate, Save)
  const [currentStep, setCurrentStep] = useState(0);
  // Positional mode: within step 1, show the figure-label sub-step instead of phase detection
  const [showFigureLabelStep, setShowFigureLabelStep] = useState(false);
  const [, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Edit mode: fetch existing template data
  const { data: existingTemplate, isLoading: isLoadingTemplate } = useTemplate(
    isEditMode ? editTemplateId : undefined
  );

  // Hydrate all state from the existing template when in edit mode
  useEffect(() => {
    if (!isEditMode || !existingTemplate) return;

    const t = existingTemplate;
    const meta = t.metadata || {};

    // Basic info
    setTemplateName(t.name || '');
    setTemplateDescription(t.description || '');
    setTemplateNotes(t.coach_notes || '');
    setMovementType(t.movement_type || meta.movement_type || '');
    setSportCategory(meta.sport_category || '');
    setAthleteLevel(meta.athlete_level || '');
    setCoachingCues(meta.coaching_cues || []);
    setCommonMistakes(meta.common_mistakes || []);
    setPerformanceNotes(meta.performance_notes || []);
    setStrictness((meta.strictness as StrictnessLevel) || 'standard');
    const _camDev = meta.camera_device || 'smartphone';
    const _knownDevices = ['smartphone', 'action_camera', 'other'];
    if (_knownDevices.includes(_camDev)) {
      setCameraDevice(_camDev);
    } else {
      setCameraDevice('other');
      setCameraDeviceOther(_camDev);
    }
    setCameraOrientation(meta.camera_orientation || 'landscape');
    // B3 — hydrate the camera angle from the closed vocabulary. Legacy
    // templates can carry `front` (the retired spelling), `behind` (a chip
    // that no longer exists) or free text stuffed into camera_angle_setup;
    // each resolves to a chip the UI still offers, and the free text moves to
    // camera_angle_other so the next save emits the clean pair.
    const _camAngle = (meta.camera_angle_setup || 'side').trim();
    const _camAngleOther = meta.camera_angle_other ?? '';
    if (_camAngle === 'front') {
      // Retired spelling — the backend's single read point normalises it too.
      setCameraAngle('frontal');
      setCameraAngleOther(_camAngleOther);
    } else if (CAMERA_ANGLE_VALUES.includes(_camAngle)) {
      setCameraAngle(_camAngle);
      setCameraAngleOther(_camAngleOther);
    } else {
      // 'behind' and any free text land here.
      setCameraAngle('other');
      setCameraAngleOther(_camAngleOther || _camAngle);
    }

    // Video
    if (t.reference_video_id) {
      setVideoId(t.reference_video_id);
    }
    setTrimStartFrame(t.trim_start_frame ?? 0);
    setTrimEndFrame(t.trim_end_frame ?? 0);

    // Phases — normalize label→name for edit mode (backend stores 'label', frontend uses 'name')
    if (t.phases && t.phases.length > 0) {
      // Backend legacy templates store the phase name under `label`; the strict
      // Phase type only declares `name`. Widen locally to read the legacy field.
      const normalized = t.phases.map((p: Phase & { label?: string }) => ({
        ...p,
        name: p.name || p.label || 'Phase',
      }));
      setDetectedPhases(normalized as Phase[]);
      setManualPhases(normalized as Phase[]);
    }

    // Annotations
    if (t.annotations && t.annotations.length > 0) {
      setAnnotations(t.annotations as Annotation[]);
    }

    // Scoring settings from metadata
    if (meta.global_body_match_enabled !== undefined) {
      setGlobalBodyMatchEnabled(meta.global_body_match_enabled);
    }
    if (meta.global_body_match_weight !== undefined) {
      setGlobalBodyMatchWeight(meta.global_body_match_weight);
    }
    if (meta.rotation_sensitive_scoring !== undefined) {
      setRotationSensitive(meta.rotation_sensitive_scoring);
    }
    // Goal Score PR-7b — hydrate the coach-authored Form-effect curve.
    // Absent/invalid stays `undefined` (resolves to Neutral at score time;
    // the Studio's own default-prefill is a SEPARATE, percentile-derived
    // default — see FormEffectCurveEditor).
    if (meta.form_effect_curve) {
      setFormEffectCurve(meta.form_effect_curve as FormEffectCurve);
    }
    // mirror_invariant_scoring is now always enabled (legacy field ignored)
    if (meta.phase_scoring_config) {
      setPhaseConfigs(meta.phase_scoring_config);
    }
    if (meta.phase_speed_intents) {
      setPhaseSpeedIntents(meta.phase_speed_intents);
    }
    // P1-1 — restore speed mode (default 'off' when absent, i.e. legacy templates)
    if (meta.speed_mode === 'on') {
      setSpeedMode('on');
    }
    // P1-4 — restore speed weight (default 0.15 when absent)
    if (typeof meta.speed_weight === 'number') {
      setSpeedWeight(meta.speed_weight);
    }
    // tracked_landmarks removed — trail configuration moved to evaluation display
    if (meta.economy_of_motion_weight !== undefined) {
      setEconomyOfMotionWeight(meta.economy_of_motion_weight);
    }
    if (meta.facing_direction) {
      // WRITER 1 (load)
      setFacing(facingFromStored(meta.facing_direction, meta.facing_source));
      // Mark facing as already dismissed so we don't re-trigger the confirmation dialog
      setFacingDismissed(true);
    }

    // Phase 2 declarative handedness
    if (t.exercise_symmetry) {
      setExerciseSymmetry(t.exercise_symmetry);
    }
    if (t.template_lead_side) {
      setTemplateLeadSide(t.template_lead_side);
    }

    // Kinetic-chain sequencing coaching flag
    if (t.enable_sequencing_coaching !== undefined) {
      setEnableSequencingCoaching(t.enable_sequencing_coaching);
    }

    // Restore focal person selection from saved template
    if (t.focal_person_id !== undefined && t.focal_person_id !== null) {
      setFocalPersonIndex(t.focal_person_id);
    } else {
      // Default to person 0 for templates saved before this feature existed
      setFocalPersonIndex(0);
    }

    // Skip upload step since video already exists; skip to phases if phases exist
    const hasPhases = t.phases && t.phases.length > 0;
    setCurrentStep(hasPhases ? 2 : 1);
  }, [isEditMode, existingTemplate]);

  // Update local metadata state when video metadata is fetched
  useEffect(() => {
    if (fetchedVideoMetadata) {
      const totalFrames = fetchedVideoMetadata.frame_count || Math.floor((fetchedVideoMetadata.duration || 10) * (fetchedVideoMetadata.fps || VIDEO_CONSTRAINTS.DEFAULT_FPS));
      setVideoMetadata({
        width: fetchedVideoMetadata.width || 640,
        height: fetchedVideoMetadata.height || 480,
        fps: fetchedVideoMetadata.fps || VIDEO_CONSTRAINTS.DEFAULT_FPS,
        totalFrames,
      });
      // In create mode, automatically use full video range (no trimming UI).
      // In edit mode, trimEndFrame is already set from the template — don't overwrite.
      if (!isEditMode) {
        setTrimStartFrame(0);
        setTrimEndFrame(totalFrames - 1);
      }
    }
  }, [fetchedVideoMetadata, isEditMode]);

  // Handle metadata loading errors
  useEffect(() => {
    if (metadataError) {
      console.error('Failed to load video metadata:', metadataError);
      setError(metadataError instanceof Error ? metadataError.message : 'Failed to load video metadata');
      if (!isEditMode) {
        setCurrentStep(0); // Go back to upload step if video not found (create mode only)
      }
    }
  }, [metadataError, isEditMode]);

  // Auto-resolve person selection when persons data arrives.
  // For single-person videos, silently select person 0 but do NOT auto-advance —
  // the coach must confirm the trim via VideoTrimTool's "Continue to Phase Detection"
  // button (B-2026-0102 Phase 1).  The trim tool renders once videoMetadata is loaded
  // and the coach clicks the trim tool's "Continue to Phase Detection" button to advance.
  useEffect(() => {
    if (!personsData) return;

    if (personsData.person_count === 1) {
      // Single person — select silently; stay on step 0 for trim confirmation
      setFocalPersonIndex(0);
    }
    // Multiple persons: leave focalPersonIndex null until coach picks one
  }, [personsData, isEditMode, currentStep]);

  // Handle facing detection state changes
  useEffect(() => {
    if (facingDetection.isAutoApplied && facingDetection.direction) {
      // WRITER 2 (auto-apply)
      setFacing(facingFromDetector(facingDetection.direction));
    }

    if (facingDetection.needsConfirmation && !showFacingConfirmDialog && !facingDismissed) {
      setShowFacingConfirmDialog(true);
    }
  }, [
    facingDetection.isAutoApplied,
    facingDetection.direction,
    facingDetection.needsConfirmation,
    showFacingConfirmDialog,
    facingDismissed,
  ]);

  // Handle facing direction confirmation
  const handleFacingConfirm = () => {
    if (facingDetection.direction) {
      facingDetection.confirmDirection(facingDetection.direction);
      // WRITER 3 (confirm) — agreeing with the detector is still a coach
      // decision, not a detector guess.
      setFacing(facingFromCoach(facingDetection.direction));
    }
    setShowFacingConfirmDialog(false);
    setFacingDismissed(true);
  };

  const handleFacingSwitch = () => {
    const oppositeDirection = facingDetection.direction === 'left' ? 'right' : 'left';
    facingDetection.confirmDirection(oppositeDirection);
    // WRITER 4 (switch) — the coach corrected the detector.
    setFacing(facingFromCoach(oppositeDirection));
    setShowFacingConfirmDialog(false);
    setFacingDismissed(true);
  };

  const handleFacingDismiss = () => {
    // If user closes without explicitly choosing, default to the detected direction
    if (facingDetection.direction && !facingDirection) {
      facingDetection.confirmDirection(facingDetection.direction);
      // WRITER 5 (dismiss) — the detected VALUE is kept, so the source has to
      // say nobody confirmed it. null would be wrong twice over: it would
      // break the joint pin AND be indistinguishable from "cleared".
      setFacing(facingFromDismissedDialog(facingDetection.direction));
    }
    setShowFacingConfirmDialog(false);
    setFacingDismissed(true);
  };

  // Step 0: Handle video upload
  const handleVideoUpload = async (uploadedVideoId: string, metadata?: VideoApiResponse | Video) => {
    setVideoId(uploadedVideoId);
    // Reset person selection when a new video is uploaded
    setFocalPersonIndex(null);

    if (metadata) {
      const totalFrames = metadata.frame_count || Math.floor((metadata.duration || 10) * (metadata.fps || VIDEO_CONSTRAINTS.DEFAULT_FPS));
      setVideoMetadata({
        width: metadata.width || 640,
        height: metadata.height || 480,
        fps: metadata.fps || VIDEO_CONSTRAINTS.DEFAULT_FPS,
        totalFrames,
      });
      // Default to full video range — the coach adjusts via VideoTrimTool
      setTrimStartFrame(0);
      setTrimEndFrame(totalFrames - 1);
    }

    // Auto-detect facing direction (non-blocking)
    facingDetection.detectFacing(uploadedVideoId).catch((err) => {
      console.warn('Facing detection failed (non-critical):', err);
    });

    // Stay on step 0 — the auto-person-resolution useEffect will advance to step 1
    // once person data loads. For single-person videos this is nearly instant.
    // For multi-person videos the PersonPicker appears and the coach clicks "Continue".
  };

  // Step 1: Handle phase detection (auto-detect for suggestions)
  const handlePhasesAccepted = (phases: Phase[]) => {
    setDetectedPhases(phases);
    // Positional mode: go to figure-label step (represented as sub-state within step 1)
    if (templateKind === 'positional') {
      setCurrentStep(1); // stay on step 1 but switch to figure labeling
      setShowFigureLabelStep(true);
    } else {
      setCurrentStep(2);
    }
  };

  const handleSkipPhaseDetection = () => {
    // User wants to start from scratch with manual phases
    setDetectedPhases([]);
    if (templateKind === 'positional') {
      setShowFigureLabelStep(true);
    } else {
      setCurrentStep(2);
    }
  };

  const handleEditDetectedPhases = (phases: Phase[]) => {
    // User wants to edit the auto-detected phases in PhaseEditor
    setDetectedPhases(phases);
    if (templateKind === 'positional') {
      setShowFigureLabelStep(true);
    } else {
      setCurrentStep(2);
    }
  };

  // Step 2 (Manual Phase Editor): Handle manual phase definition
  // Memoize to prevent infinite re-render loops in PhaseEditor
  const handleManualPhasesChange = useCallback((phases: Phase[]) => {
    setManualPhases(phases);
  }, []);

  const handleManualPhasesNext = () => {
    if (manualPhases.length < 2) {
      setError('Please define at least 2 phases before continuing.');
      return;
    }
    setError(null);
    // Step 2 now handles scoring config inline; proceed straight to annotations (step 3)
    // In positional mode, PerFigureAnnotationTabs manages this internally.
    setCurrentStep(3);
  };

  // Step 2 inline: Handle phase scoring configuration (called by PhaseEditor's onConfigChange)
  const handlePhaseConfigChange = useCallback((configs: PhaseConfig[]) => {
    setPhaseConfigs(configs);
  }, []);

  // Step 3: Handle per-phase speed intent changes (from ScoringStudio).
  // P1-4: Reset speedAcknowledged in edit mode so the preview re-fetches.
  const handlePhaseSpeedIntentChange = useCallback(
    (phaseId: string, intent: 'match' | 'reward_faster' | 'reward_slower' | 'off') => {
      setPhaseSpeedIntents((prev) => ({ ...prev, [phaseId]: intent }));
      // Only require re-acknowledgment in edit mode where a preview is possible.
      if (isEditMode) setSpeedAcknowledged(false);
    },
    [isEditMode]
  );

  // P1-4: Handle speed mode changes — reset acknowledgment when turning on in edit mode.
  const handleSpeedModeChange = useCallback(
    (mode: 'off' | 'on') => {
      setSpeedMode(mode);
      // Turning speed ON in edit mode: coach must acknowledge the score impact preview.
      // Create mode: no saved eval exists, so no preview is needed and ack stays true.
      if (mode === 'on' && isEditMode) setSpeedAcknowledged(false);
    },
    [isEditMode]
  );

  // P1-4: Handle speed weight changes — reset acknowledgment in edit mode.
  const handleSpeedWeightChange = useCallback(
    (weight: number) => {
      setSpeedWeight(weight);
      if (isEditMode && speedMode === 'on') setSpeedAcknowledged(false);
    },
    [isEditMode, speedMode]
  );

  // Step 3: Handle annotation completion
  const handleAnnotationsComplete = () => {
    // Allow proceeding without annotations if Global Body Matching is set to 100%
    const canSkipAnnotations = globalBodyMatchEnabled && globalBodyMatchWeight >= 1.0;

    if (annotations.length === 0 && !canSkipAnnotations) {
      setError('Please add at least one annotation before continuing, or set Body Form Weight to 100%.');
      return;
    }

    // P1-4 R7 safeguard: require acknowledgment of the score preview when speed is
    // enabled in edit mode (create mode has no saved eval to diff against).
    if (speedMode === 'on' && !speedAcknowledged && isEditMode) {
      setError('Please review and acknowledge the speed scoring impact above before continuing.');
      return;
    }

    setError(null);
    setCurrentStep(4);
  };

  // Step 4: Handle template save (create or update)
  const handleSaveTemplate = async () => {
    if (isSaving) return;

    if (!videoId || !templateName) {
      setError('Please provide a template name.');
      return;
    }

    // In positional mode, phases are stored per-figure (figureStates) not in manualPhases.
    if (templateKind === 'solo' && manualPhases.length < 2) {
      setError('Please define at least 2 phases before saving the template.');
      return;
    }

    // In positional mode, validate that we have 2–5 figures with phases + annotations.
    if (templateKind === 'positional') {
      if (figureSelections.length < 2 || figureSelections.length > 5) {
        setError(`Positional templates require 2–5 figures (currently ${figureSelections.length}).`);
        return;
      }
      for (const fig of figureSelections) {
        const state = figureStates[fig.person_index];
        if (!state || state.phases.length < 2) {
          setError(`Figure "${fig.position_label}" needs at least 2 phases. Please complete it before saving.`);
          return;
        }
      }
    }

    if ((exerciseSymmetry === 'asymmetric' || exerciseSymmetry === 'rotational') && !templateLeadSide) {
      setError('Please select the lead side (starting position) for this movement.');
      return;
    }

    setIsSaving(true);
    setIsLoading(true);
    setError(null);

    try {
      const resolvedMovementType = movementType || templateName;
      const mappedPhases = manualPhases.map((phase, index) => ({
        ...phase,
        label: phase.name || (phase as Phase & { label?: string }).label || `Phase ${index + 1}`,
      }));

      // Build figures array for positional templates (PTD-3)
      const figuresPayload: FigureCreate[] | undefined =
        templateKind === 'positional' && figureSelections.length >= 2
          ? figureSelections.map((fig) => {
              const state = figureStates[fig.person_index];
              const figurePhaseMaps = (state?.phases ?? []).map((phase, index) => ({
                ...phase,
                label: phase.name || (phase as Phase & { label?: string }).label || `Phase ${index + 1}`,
              }));
              return {
                position_label: fig.position_label,
                reference_video_id: videoId!,
                focal_person_id: fig.person_index,
                trim_start_frame: state?.trimStartFrame ?? trimStartFrame,
                trim_end_frame: state?.trimEndFrame ?? trimEndFrame,
                annotations: state?.annotations.length ? state.annotations : null,
                phases: figurePhaseMaps.length ? figurePhaseMaps : null,
                coach_notes: state?.coach_notes.trim() || null,
                coaching_cues: state?.coaching_cues.length ? state.coaching_cues : null,
              };
            })
          : undefined;

      const payload = {
        name: templateName,
        description: templateDescription,
        reference_video_id: videoId,
        movement_type: resolvedMovementType,
        trim_start_frame: trimStartFrame,
        trim_end_frame: trimEndFrame,
        focal_person_id: focalPersonIndex ?? 0,
        coach_notes: templateNotes.trim() || undefined,
        // Phase 2 declarative handedness fields (top-level)
        exercise_symmetry: exerciseSymmetry || undefined,
        template_lead_side: ((exerciseSymmetry === 'asymmetric' || exerciseSymmetry === 'rotational') && templateLeadSide) ? templateLeadSide : null,
        // Kinetic-chain sequencing coaching flag (opt-in, default false)
        enable_sequencing_coaching: enableSequencingCoaching,
        // Optional YouTube reference link (YTI-1)
        youtube_url: youtubeUrl.trim() || null,
        // F3: facing direction — also sent top-level so backend validates it.
        // Also in metadata.facing_direction for backward compat with existing pipeline reads.
        facing_direction: facingDirection || null,
        // Positional template fields (PTD-3) — only present for positional kind
        ...(templateKind === 'positional' ? { template_kind: 'positional' as const } : {}),
        ...(figuresPayload ? { figures: figuresPayload } : {}),
        annotations: templateKind === 'solo' ? annotations : undefined,
        phases: templateKind === 'solo' ? mappedPhases : undefined,
        metadata: {
          total_frames: trimEndFrame - trimStartFrame + 1,
          fps: videoMetadata.fps,
          facing_direction: facing.direction,
          // Joint pin (Phase 1 contract): null if and only if
          // facing_direction is null — guaranteed by FacingState's writers.
          facing_source: facing.source,
          facing_confidence: facingDetection.confidence,
          handedness: templateHandedness,
          // Keep phases and movement_type in metadata for backwards compatibility
          phases: templateKind === 'solo' ? mappedPhases : undefined,
          movement_type: resolvedMovementType,
          // Qualitative context for LLM coaching system
          sport_category: sportCategory || undefined,
          athlete_level: athleteLevel || undefined,
          coaching_cues: coachingCues.length > 0 ? coachingCues : undefined,
          common_mistakes: commonMistakes.length > 0 ? commonMistakes : undefined,
          performance_notes: performanceNotes.length > 0 ? performanceNotes : undefined,
          // Global body match settings
          global_body_match_enabled: globalBodyMatchEnabled,
          global_body_match_weight: globalBodyMatchEnabled ? globalBodyMatchWeight : 0,
          rotation_sensitive_scoring: rotationSensitive,
          // Goal Score PR-7b — the coach-authored Form-effect curve. Absent
          // (undefined) when the coach has never opened the curve editor —
          // resolves to Neutral at score time
          // (app/models/form_effect.py::resolve_form_effect_curve), never
          // written as a guessed default here.
          form_effect_curve: formEffectCurve ?? undefined,
          // Economy of motion
          economy_of_motion_weight: economyOfMotionWeight,
          // Phase scoring configuration
          phase_scoring_config: phaseConfigs,
          // Per-phase speed intent
          phase_speed_intents: Object.keys(phaseSpeedIntents).length > 0 ? phaseSpeedIntents : undefined,
          // P1-1 speed scoring master switch. Only written when 'on'; absent = 'off'
          // (byte-identical for all existing templates).
          // P1-4 speed_weight: written alongside speed_mode so it's absent when off.
          // PRESERVE: speed_weight must be absent when speed_mode is absent (byte-identical).
          ...(speedMode === 'on' ? { speed_mode: 'on' as const, speed_weight: speedWeight } : {}),
          // Trail configuration moved to evaluation display — no template-side storage needed
          mirror_invariant_scoring: true,  // Always enabled
          strictness: strictness,
          camera_device: (cameraDevice === 'other' && cameraDeviceOther.trim())
            ? cameraDeviceOther.trim() : cameraDevice || undefined,
          camera_orientation: cameraOrientation || undefined,
          // B3 — camera-angle vocabulary. `camera_angle_setup` carries ONLY
          // the closed set the backend recognises ('side' | 'frontal' |
          // 'diagonal' | 'other'); free text goes to its own key. Stuffing the
          // coach's free text into this key is what defeated the narrative
          // gate and the direction resolver, and `front` (never recognised)
          // is now emitted as `frontal`.
          camera_angle_setup: cameraAngle || undefined,
          camera_angle_other:
            cameraAngle === 'other' && cameraAngleOther.trim()
              ? cameraAngleOther.trim()
              : null,
        },
      };

      if (isEditMode && editTemplateId) {
        // Update existing template
        await updateTemplate(editTemplateId, payload);
        // FE-4 — link the coach instruction in the EDIT branch too.
        //
        // This call used to exist only in the create branch, so adding coach
        // instruction to an EXISTING template produced suggestions that worked
        // for the rest of the session and then vanished: the teaching video was
        // never attached, `TemplateDetails` had no recording to play, and the
        // row was eventually removed by `_purge_unlinked_teaching_videos`.
        // `useLinkTeachingVideo` existed for exactly this and had zero call
        // sites. The direct service call is used here rather than that hook
        // because this handler is an imperative save sequence, not a render —
        // the same reason the create branch below calls it directly.
        if (teachingVideoId) {
          try {
            await linkTeachingVideoToTemplate(editTemplateId, teachingVideoId);
          } catch {
            // The template itself is saved. Say so, say what is missing, and
            // point at the one place it can be fixed — which, now that the edit
            // branch links, is this editor.
            setError(TEACHING_VIDEO_LINK_FAILED);
            queryClient.invalidateQueries({ queryKey: ['templates', editTemplateId] });
            queryClient.invalidateQueries({ queryKey: ['templates'] });
            return;
          }
        }
        // Invalidate cached template data so TemplateDetails shows fresh data
        queryClient.invalidateQueries({ queryKey: ['templates', editTemplateId] });
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        navigate(`/templates/${editTemplateId}`);
      } else {
        // Create new template via API
        // Pass channelIdFromQuery so the backend associates the template with the channel at creation time.
        const result = await createTemplate(payload, channelIdFromQuery);
        const template = result.template || result;

        if (!template?.id) {
          throw new Error('Server returned invalid response. Please try again.');
        }

        // Link the teaching video to the new template if one was uploaded.
        // The link endpoint (POST /api/templates/{id}/teaching-video) must be
        // called separately — the create endpoint does not accept teaching_video_id.
        if (teachingVideoId) {
          try {
            await linkTeachingVideoToTemplate(template.id, teachingVideoId);
          } catch {
            // Template is already saved — show a recoverable error and still
            // navigate. FE-14: the old sentence sent the coach to "the template
            // details page", which has no attach action at all — it only PLAYS
            // a linked video. The re-entry point is this editor, and as of FE-4
            // above its edit branch actually links.
            setError(TEACHING_VIDEO_LINK_FAILED);
            // Redirect to channel page if channel context existed, else template details
            navigate(channelSlugFromQuery ? `/channels/${channelSlugFromQuery}` : `/templates/${template.id}`);
            return;
          }
        }

        // Redirect to the channel page if launched from channel context, else to template details
        if (channelSlugFromQuery) {
          navigate(`/channels/${channelSlugFromQuery}`);
        } else {
          navigate(`/templates/${template.id}`);
        }
      }
    } catch (err) {
      console.error('Failed to save template:', err);
      if (axios.isAxiosError(err) && err.response?.data) {
        const data = err.response.data;
        const detail = data.errors?.join('; ') || data.message || data.detail || err.message;
        setError(detail);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save template. Please try again.');
      }
    } finally {
      setIsLoading(false);
      setIsSaving(false);
    }
  };

  // Render step content based on currentStep
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        // In edit mode step 0: read-only video summary + trim tool so coach can re-trim.
        // The reference video itself cannot be changed.
        if (isEditMode) {
          return (
            <Card>
              <CardHeader>
                <CardTitle>Reference Video &amp; Clip Boundaries</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert variant="info">
                  The reference video cannot be changed when editing a template. You can
                  adjust the clip boundaries below — phase detection will use the updated range.
                </Alert>
                <p className="text-sm text-text-secondary">
                  Video ID: <span className="font-medium font-mono text-text-primary">{videoId}</span>
                </p>

                {/* Trim tool for edit mode — seeded from existing template trim values.
                    The existing trim is already hydrated (line ~271: setTrimStartFrame/End),
                    so we just render with whatever is in state. */}
                {videoId && videoMetadata.totalFrames > 0 && videoStreamUrl ? (
                  <VideoTrimTool
                    videoUrl={videoStreamUrl}
                    totalFrames={videoMetadata.totalFrames}
                    fps={videoMetadata.fps}
                    trimStart={trimStartFrame}
                    trimEnd={trimEndFrame > 0 ? trimEndFrame : videoMetadata.totalFrames - 1}
                    onTrimChange={(start, end) => {
                      setTrimStartFrame(start);
                      setTrimEndFrame(end);
                    }}
                    onConfirm={() => setCurrentStep(1)}
                  />
                ) : (
                  <Button onClick={() => setCurrentStep(1)}>
                    Continue to Phase Detection
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        }
        return (
          <Card>
            <CardHeader>
              <CardTitle>Upload Reference Video</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="info">
                Upload the video showing the ideal form for this movement — then set your clip boundaries below.
              </Alert>
              <div className="rounded-lg border border-border-subtle bg-dark-elevated-1 p-3 space-y-2">
                <p className="text-xs font-medium text-text-secondary">Filming Tips</p>
                <ul className="text-xs text-text-muted space-y-1 list-disc list-inside">
                  <li>Use a <strong>tripod or stable surface</strong> — camera shake reduces scoring accuracy</li>
                  <li>Keep the phone <strong>level and upright</strong> — do not tilt or rotate the camera during filming</li>
                  <li>Film from the <strong>side or diagonal</strong> for best pose detection results</li>
                  <li>Ensure <strong>full body is visible</strong> throughout the entire movement</li>
                  <li>Athletes will need to <strong>match this camera angle</strong> when recording their evaluations</li>
                </ul>
              </div>
              {/* Positional team-drill mode toggle (PTD-3) */}
              <div className="rounded-lg border border-border-subtle bg-dark-elevated-1 p-4 space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary">
                      Positional Team Drill
                      <span className="ml-1.5 px-1.5 py-0.5 text-xs font-medium bg-accent/15 text-accent rounded">
                        beta
                      </span>
                    </p>
                    <p className="text-xs text-text-muted mt-0.5">
                      Label multiple performers in the reference video as distinct positions
                      (e.g. PG, SG, GK, LW). Each figure gets its own annotation and phases.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    id="positional-mode-toggle"
                    aria-checked={templateKind === 'positional'}
                    onClick={() => {
                      setTemplateKind(templateKind === 'positional' ? 'solo' : 'positional');
                      setFigureSelections([]);
                      setFigureStates({});
                      setShowFigureLabelStep(false);
                    }}
                    className={[
                      'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full',
                      'border-2 border-transparent transition-colors duration-200 ease-in-out',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                      templateKind === 'positional' ? 'bg-accent' : 'bg-dark-elevated-2',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'pointer-events-none inline-block h-4 w-4 transform rounded-full',
                        'bg-white shadow ring-0 transition duration-200 ease-in-out',
                        templateKind === 'positional' ? 'translate-x-4' : 'translate-x-0',
                      ].join(' ')}
                    />
                  </button>
                </div>
              </div>

              <VideoUploader
                videoType="template"
                onUploadComplete={(id, metadata) => handleVideoUpload(id, metadata)}
              />

              {/* Person picker — shown after upload when multiple people are detected */}
              {videoId && personsData && personsData.person_count > 1 && (
                <div className="space-y-4">
                  <PersonPicker
                    videoId={videoId}
                    persons={personsData.persons}
                    sampleFrameUrl={personsData.sample_frame_url}
                    selectedIndex={focalPersonIndex}
                    onSelect={setFocalPersonIndex}
                    label="Select the coach to track"
                  />
                </div>
              )}

              {/* Loading state while person detection runs */}
              {videoId && isLoadingPersons && (
                <p className="text-sm text-text-muted">Detecting persons in video...</p>
              )}

              {/* Video trim tool — shown once metadata is available and person is resolved.
                  The coach sets clip boundaries here and clicks "Continue to Phase Detection"
                  to advance the wizard. (B-2026-0102 Phase 1)
                  COORDINATE INVARIANT: trimStartFrame/trimEndFrame are always ABSOLUTE
                  raw-video frame numbers.  PhaseEditor receives trim-relative totalFrames
                  (trimEndFrame - trimStartFrame + 1) — that invariant is untouched here.   */}
              {videoId && videoMetadata.totalFrames > 0 && !isLoadingPersons && videoStreamUrl &&
                (personsData === undefined || personsData.person_count === 1 || focalPersonIndex !== null) && (
                <VideoTrimTool
                  videoUrl={videoStreamUrl}
                  totalFrames={videoMetadata.totalFrames}
                  fps={videoMetadata.fps}
                  trimStart={trimStartFrame}
                  trimEnd={trimEndFrame}
                  onTrimChange={(start, end) => {
                    setTrimStartFrame(start);
                    setTrimEndFrame(end);
                  }}
                  onConfirm={() => setCurrentStep(1)}
                />
              )}
            </CardContent>
          </Card>
        );

      case 1:
        // In positional mode, the figure-label step is shown after phase detection.
        if (templateKind === 'positional' && showFigureLabelStep) {
          return personsData ? (
            <FigureLabelStep
              persons={personsData.persons}
              initialSelections={figureSelections.length > 0 ? figureSelections : undefined}
              onConfirm={(selections) => {
                setFigureSelections(selections);
                // Initialize figure states for any newly selected persons
                setFigureStates((prev) => {
                  const next = { ...prev };
                  for (const s of selections) {
                    if (!next[s.person_index]) {
                      next[s.person_index] = emptyFigureState(trimStartFrame, trimEndFrame);
                    }
                  }
                  return next;
                });
                setCurrentStep(2);
              }}
              onBack={() => setShowFigureLabelStep(false)}
            />
          ) : null;
        }

        // In edit mode, allow going to phase detection to re-detect if desired.
        // Pass trim bounds so detection runs on the coach-defined clip (B-2026-0102 Phase 1).
        return videoId ? (
          <PhaseDetectionPreview
            videoId={videoId}
            fps={videoMetadata.fps}
            totalFrames={videoMetadata.totalFrames}
            onPhasesAccepted={handlePhasesAccepted}
            onSkip={handleSkipPhaseDetection}
            onEditPhases={handleEditDetectedPhases}
            trimStartFrame={trimStartFrame > 0 ? trimStartFrame : undefined}
            trimEndFrame={trimEndFrame < videoMetadata.totalFrames - 1 ? trimEndFrame : undefined}
          />
        ) : null;

      case 2:
        // Positional mode: per-figure annotation (phases + scoring) handled here
        if (templateKind === 'positional' && figureSelections.length >= 2) {
          return videoId ? (
            <PerFigureAnnotationTabs
              figures={figureSelections}
              figureStates={figureStates}
              onFigureStateChange={handleFigureStateChange}
              videoId={videoId}
              videoMetadata={videoMetadata}
              templateHandedness={templateHandedness}
              cameraAngle={cameraAngle}
              facingDirection={facingDirection}
              onNext={() => setCurrentStep(4)}
              onBack={() => {
                setShowFigureLabelStep(true);
                setCurrentStep(1);
              }}
            />
          ) : null;
        }

        return videoId && videoStreamUrl ? (
          <PhaseEditor
            videoId={videoId}
            videoUrl={videoStreamUrl}
            fps={videoMetadata.fps}
            totalFrames={trimEndFrame - trimStartFrame + 1}
            initialPhases={manualPhases.length > 0 ? manualPhases : (detectedPhases.length > 0 ? detectedPhases : undefined)}
            onPhasesChange={handleManualPhasesChange}
            onNext={handleManualPhasesNext}
            onBack={() => setCurrentStep(1)}
            onConfigChange={handlePhaseConfigChange}
            initialConfigs={phaseConfigs.length > 0 ? phaseConfigs : undefined}
          />
        ) : null;

      // Step 3: Scoring Studio (formerly separate Annotate + GlobalBodyMatch steps)
      case 3:
        return videoId ? (
          <div className="space-y-4">
            {/* Coach Instruction Extraction (optional, paid coaches only) */}
            <TeachingVideoUploader
              user={user}
              sport={sportCategory}
              movement={movementType || templateName}
              onComplete={(id) => setTeachingVideoId(id)}
              onSkip={() => setTeachingVideoId(null)}
            />

            {/* YouTube reference video (YTI) — link + transcript import.
                Importing sets teachingVideoId, so the suggestion panel below
                processes it exactly like an uploaded/pasted teaching video.
                The URL is also persisted as youtube_url for the embed player. */}
            <Card>
              <CardContent className="space-y-2 pt-6">
                <Input
                  label="YouTube reference video (optional)"
                  type="url"
                  value={youtubeUrl}
                  onChange={(e) => {
                    setYoutubeUrl(e.target.value);
                    setYtImportStatus({ type: 'idle' });
                  }}
                  placeholder="https://youtube.com/watch?v=..."
                />
                <p className="text-xs text-text-muted">
                  Embeds a reference player on the template.
                  {user?.can_import_youtube
                    ? ' Optionally import the video’s transcript to auto-suggest coaching cues and goals below.'
                    : ' To seed coaching cues, paste your notes (or the video’s transcript) below.'}
                </p>
                {youtubeUrl.trim() && user?.can_import_youtube && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={handleImportYoutubeTranscript}
                      disabled={ytImportStatus.type === 'loading'}
                      className="text-xs px-3 py-1.5 rounded-md bg-dark-elevated-2 text-text-secondary hover:text-text-primary border border-border-subtle disabled:opacity-50 transition-colors"
                    >
                      {ytImportStatus.type === 'loading' ? 'Importing…' : 'Import transcript'}
                    </button>
                    {ytImportStatus.message && (
                      <span
                        className={cn(
                          'text-xs',
                          ytImportStatus.type === 'success'
                            ? 'text-success'
                            : ytImportStatus.type === 'error'
                              ? 'text-warning'
                              : 'text-text-muted',
                        )}
                        role={ytImportStatus.type === 'error' ? 'alert' : undefined}
                      >
                        {ytImportStatus.message}
                      </span>
                    )}
                  </div>
                )}
                {youtubeUrl.trim() && !user?.can_import_youtube && (
                  <YoutubeTranscriptFallbackNotice paidCoach={isPaidCoach(user)} />
                )}
              </CardContent>
            </Card>

            {/* §D — align the coaching video's phases to the template phases so
                accepted checks scope to the correct phase. Creation-time only;
                renames phase names (ids/frames preserved) → score-neutral.
                RENDERED ABOVE the suggestion panel on purpose: the coach has to
                be able to line the names up BEFORE accepting any checks, or
                every check lands on the whole video instead of its phase. On
                the live 30-suggestion run that motivated PR-3, 28 of 30 did
                exactly that (audit item E2E-2). */}
            {teachingVideoId && manualPhases.length > 0 && (
              <PhaseReconciliationPanel
                teachingVideoId={teachingVideoId}
                templatePhases={manualPhases}
                onApplyNames={handleManualPhasesChange}
              />
            )}

            {/* Suggestion panel — shown once extraction completes */}
            {teachingVideoId && (
              <Card>
                <CardHeader>
                  <CardTitle>What we heard in your instruction</CardTitle>
                  {/* FE-3: the old line here said "Accept a goal to open its
                      config form pre-filled." No form ever opened. */}
                  <p className="text-sm text-text-secondary mt-1">
                    Add the checks you want, and click a cue or a mistake to put
                    it on the template. Each check you add appears in your
                    checkpoint list below.
                  </p>
                </CardHeader>
                <CardContent>
                  <AnnotationSuggestionPanel
                    teachingVideoId={teachingVideoId}
                    templatePhases={manualPhases}
                    onAnnotationsMaterialized={handleAnnotationsMaterialized}
                    onAddCue={handleAddCueFromTeachingVideo}
                    onAddMistake={handleAddMistakeFromTeachingVideo}
                  />
                </CardContent>
              </Card>
            )}

            {videoStreamUrl ? (
              <ScoringStudio
                videoId={videoId}
                videoUrl={videoStreamUrl}
                fps={videoMetadata.fps}
                totalFrames={videoMetadata.totalFrames}
                videoWidth={videoMetadata.width}
                videoHeight={videoMetadata.height}
                annotations={annotations}
                onAnnotationsChange={setAnnotations}
                templateHandedness={templateHandedness}
                detectedLeadSide={(templateLeadSide || facingDetection.direction) || undefined}
                phases={manualPhases}
                facingDirection={facingDirection || undefined}
                globalBodyMatchEnabled={globalBodyMatchEnabled}
                onGlobalBodyMatchEnabledChange={setGlobalBodyMatchEnabled}
                globalBodyMatchWeight={globalBodyMatchWeight}
                onGlobalBodyMatchWeightChange={setGlobalBodyMatchWeight}
                rotationSensitive={rotationSensitive}
                onRotationSensitiveChange={setRotationSensitive}
                economyOfMotionWeight={economyOfMotionWeight}
                onEconomyOfMotionWeightChange={setEconomyOfMotionWeight}
                phaseSpeedIntents={phaseSpeedIntents}
                onPhaseSpeedIntentChange={handlePhaseSpeedIntentChange}
                cameraView={cameraAngle}
                speedMode={speedMode}
                onSpeedModeChange={handleSpeedModeChange}
                speedWeight={speedWeight}
                onSpeedWeightChange={handleSpeedWeightChange}
                templateId={editTemplateId ?? undefined}
                speedAcknowledged={speedAcknowledged}
                onSpeedAcknowledgedChange={setSpeedAcknowledged}
                // PR-4: the DECLARED lead side, and only where declaring one
                // actually resolves a focus area (plan §C.3a's fail-closed
                // table, encoded once in `declaredSideForFocusAreas`).
                // Deliberately NOT `detectedLeadSide` above, which falls back
                // to detection for the canvas previews — a detection is not a
                // declaration and must not read to the coach as one.
                declaredLeadSide={declaredSideForFocusAreas(exerciseSymmetry, templateLeadSide)}
                // FE-3 — open the check that was just added, filled in.
                openCheckpointId={openCheckpointId}
                onCheckpointOpened={() => setOpenCheckpointId(null)}
                // Goal Score PR-7b — the Goals section's Form-effect curve.
                formEffectCurve={formEffectCurve}
                onFormEffectCurveChange={setFormEffectCurve}
              />
            ) : (
              <p className="text-sm text-text-muted">Loading video…</p>
            )}

            {error && <Alert variant="error">{error}</Alert>}
            {annotations.length > 0 && (
              <Alert variant="success">
                {annotations.length} annotation{annotations.length !== 1 ? 's' : ''} created.
              </Alert>
            )}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleAnnotationsComplete}
                disabled={annotations.length === 0 && !(globalBodyMatchEnabled && globalBodyMatchWeight >= 1.0)}
                className="flex-1"
              >
                Continue to Save
              </Button>
            </div>
          </div>
        ) : null;

      // Step 4: Save (formerly step 5)
      case 4:
        return (
          <Card>
            <CardHeader className="bg-success/10">
              <CardTitle className="text-xl">
                {isEditMode ? 'Save Changes' : 'Save Template'}
              </CardTitle>
              <p className="text-sm text-text-secondary mt-1">
                {isEditMode
                  ? 'Review and save your changes to this movement template.'
                  : 'Enter a name and save your movement template.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <Input
                label="Template Name *"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Perfect Squat Form"
                required
                autoFocus
              />
              <div className="space-y-2">
                <label htmlFor="movement-type" className="text-sm font-medium text-text-secondary">
                  Movement Type
                </label>
                <select
                  id="movement-type"
                  value={movementType}
                  onChange={(e) => setMovementType(e.target.value)}
                  className="w-full px-3 py-2 border border-border-subtle rounded-sm-control bg-dark-elevated-2 text-text-primary focus:ring-accent focus:border-accent text-sm"
                >
                  <option value="">Select movement type (or leave to use template name)</option>
                  {MOVEMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="sport-category" className="text-sm font-medium text-text-secondary">
                  Sport Category
                </label>
                <select
                  id="sport-category"
                  value={sportCategory}
                  onChange={(e) => setSportCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-border-subtle rounded-sm-control bg-dark-elevated-2 text-text-primary focus:ring-accent focus:border-accent text-sm"
                >
                  <option value="">Select sport category (optional)</option>
                  {SPORT_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">
                  Athlete Level
                </label>
                <div className="flex gap-2">
                  {ATHLETE_LEVELS.map((level) => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setAthleteLevel(athleteLevel === level ? '' : level)}
                      className={`px-4 py-2 rounded-sm-control text-sm font-medium border transition-colors ${
                        athleteLevel === level
                          ? 'bg-accent text-text-inverse border-accent'
                          : 'bg-dark-elevated-2 text-text-primary border-border-subtle hover:bg-dark-elevated-1'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <StrictnessSelector value={strictness} onChange={setStrictness} />

              {/* Kinetic-chain sequencing coaching (opt-in, default off) */}
              <div className="flex items-start justify-between gap-4 py-1">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary">
                    Kinetic-chain sequencing coaching <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-accent/15 text-accent rounded">beta</span>
                  </p>
                  <p className="text-xs text-text-muted mt-0.5">
                    Adds coaching feedback about the <em>order</em> your body segments move (e.g.
                    core-before-arm, hip-before-knee). Best for movements with a clear sequential
                    chain — lunges, throws, strikes — recorded at a higher frame rate. It only adds
                    coaching tips, it does NOT change the score.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  id="enable-sequencing-coaching"
                  aria-checked={enableSequencingCoaching}
                  onClick={() => setEnableSequencingCoaching(!enableSequencingCoaching)}
                  className={[
                    'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full',
                    'border-2 border-transparent transition-colors duration-200 ease-in-out',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                    enableSequencingCoaching ? 'bg-accent' : 'bg-dark-elevated-2',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'pointer-events-none inline-block h-4 w-4 transform rounded-full',
                      'bg-white shadow ring-0 transition duration-200 ease-in-out',
                      enableSequencingCoaching ? 'translate-x-4' : 'translate-x-0',
                    ].join(' ')}
                  />
                </button>
              </div>

              {/* Movement Symmetry (Phase 2 declarative handedness) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-secondary">
                  Movement Type
                </label>
                <div className="flex gap-2 flex-wrap">
                  {(['symmetric', 'asymmetric', 'rotational'] as const).map((sym) => {
                    const labels: Record<string, string> = {
                      symmetric: 'Symmetric',
                      asymmetric: 'Asymmetric',
                      rotational: 'Rotational',
                    };
                    return (
                      <button
                        key={sym}
                        type="button"
                        onClick={() => {
                          setExerciseSymmetry(exerciseSymmetry === sym ? '' : sym);
                          if (sym === 'symmetric') setTemplateLeadSide('');
                        }}
                        className={`px-4 py-2 rounded-sm-control text-sm font-medium border transition-colors ${
                          exerciseSymmetry === sym
                            ? 'bg-accent text-text-inverse border-accent'
                            : 'bg-dark-elevated-2 text-text-primary border-border-subtle hover:bg-dark-elevated-1'
                        }`}
                      >
                        {labels[sym]}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-text-muted">
                  {exerciseSymmetry === 'symmetric' && 'Both sides move together — the movement is identical regardless of lead side (squat, deadlift, plank, jumping jack).'}
                  {exerciseSymmetry === 'asymmetric' && 'Clear lead side that stays consistent throughout — one side drives the movement from start to finish (fencing lunge, tennis serve, soccer kick).'}
                  {exerciseSymmetry === 'rotational' && 'Lead side at setup, but the body rotates 360°+ during execution — the starting side matters even though it changes role mid-movement (golf swing, discus throw, baseball swing, basketball spin move).'}
                  {!exerciseSymmetry && 'Choose the movement pattern that best describes how left and right sides are used.'}
                </p>
              </div>

              {/* Lead Side — shown when asymmetric or rotational */}
              {(exerciseSymmetry === 'asymmetric' || exerciseSymmetry === 'rotational') && (
                <div className="space-y-2">
                  <label htmlFor="template-lead-side" className="text-sm font-medium text-text-secondary">
                    Lead Side in Reference Video <span className="text-danger">*</span>
                  </label>
                  <select
                    id="template-lead-side"
                    value={templateLeadSide}
                    onChange={(e) => setTemplateLeadSide(e.target.value as 'left' | 'right' | '')}
                    required
                    className="w-full px-3 py-2 border border-border-subtle rounded-sm-control bg-dark-elevated-2 text-text-primary focus:ring-accent focus:border-accent text-sm"
                  >
                    <option value="">Select lead side</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                  <p className="text-xs text-text-muted">
                    {exerciseSymmetry === 'rotational'
                      ? 'Which side initiates the rotation in your reference video? For a golf swing, this is the trail hand side (right for a right-handed golfer). For a discus throw, this is the throwing arm side.'
                      : 'Which side leads the movement in your reference video? For a fencer\'s lunge, this is the dominant hand side that holds the weapon. For a kicker\'s drill, this is the kicking foot.'}
                  </p>
                </div>
              )}

              {/* Camera Setup */}
              <div className="space-y-3 pt-1">
                <div>
                  <label className="text-sm font-medium text-text-secondary">Camera Setup (optional)</label>
                  <p className="text-xs text-text-muted mt-0.5">
                    Helps athletes replicate your filming position for accurate scoring.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-text-muted uppercase tracking-wide">Device</label>
                  <div className="flex gap-2 flex-wrap">
                    {(['smartphone', 'action_camera', 'other'] as const).map((device) => {
                      const labels: Record<string, string> = { smartphone: 'Smartphone', action_camera: 'Action Camera', other: 'Other' };
                      return (
                        <button
                          key={device}
                          type="button"
                          onClick={() => setCameraDevice(cameraDevice === device ? '' : device)}
                          className={`px-3 py-1.5 rounded-sm-control text-sm font-medium border transition-colors ${
                            cameraDevice === device
                              ? 'bg-accent text-text-inverse border-accent'
                              : 'bg-dark-elevated-2 text-text-primary border-border-subtle hover:bg-dark-elevated-1'
                          }`}
                        >
                          {labels[device]}
                        </button>
                      );
                    })}
                    {cameraDevice === 'other' && (
                      <input
                        type="text"
                        value={cameraDeviceOther}
                        onChange={(e) => setCameraDeviceOther(e.target.value.slice(0, 50))}
                        placeholder="e.g. DSLR, Webcam..."
                        maxLength={50}
                        className="px-3 py-1.5 rounded-sm-control text-sm border border-border-subtle bg-dark-elevated-2 text-text-primary placeholder-text-muted focus:outline-none focus:border-accent w-40"
                      />
                    )}
                  </div>
                </div>
                {cameraDevice === 'smartphone' && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-text-muted uppercase tracking-wide">Orientation</label>
                    <div className="flex gap-2">
                      {(['landscape', 'portrait'] as const).map((orientation) => (
                        <button
                          key={orientation}
                          type="button"
                          onClick={() => setCameraOrientation(cameraOrientation === orientation ? '' : orientation)}
                          className={`px-3 py-1.5 rounded-sm-control text-sm font-medium border transition-colors ${
                            cameraOrientation === orientation
                              ? 'bg-accent text-text-inverse border-accent'
                              : 'bg-dark-elevated-2 text-text-primary border-border-subtle hover:bg-dark-elevated-1'
                          }`}
                        >
                          {orientation.charAt(0).toUpperCase() + orientation.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-text-muted uppercase tracking-wide">Camera Angle</label>
                  <div className="flex flex-wrap gap-2">
                    {CAMERA_ANGLE_OPTIONS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        /* Non-clearing (B3): clicking the selected chip used to
                           blank the field, and a blank camera angle is what
                           the backend reads as "unknown" — a coach could
                           silently un-set it with one stray click. */
                        onClick={() => setCameraAngle(value)}
                        className={`px-3 py-1.5 rounded-sm-control text-sm font-medium border transition-colors ${
                          cameraAngle === value
                            ? 'bg-accent text-text-inverse border-accent'
                            : 'bg-dark-elevated-2 text-text-primary border-border-subtle hover:bg-dark-elevated-1'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                    {cameraAngle === 'other' && (
                      <input
                        type="text"
                        value={cameraAngleOther}
                        onChange={(e) => setCameraAngleOther(e.target.value.slice(0, 50))}
                        placeholder="e.g. Elevated, Low angle..."
                        maxLength={50}
                        className="px-3 py-1.5 rounded-sm-control text-sm border border-border-subtle bg-dark-elevated-2 text-text-primary placeholder-text-muted focus:outline-none focus:border-accent w-48"
                      />
                    )}
                  </div>
                </div>

                {/* Performer facing direction selector.
                    Rendered whenever the camera angle is anything but Front —
                    side, diagonal, other, and the not-yet-chosen case. That
                    predicate is a deliberate SUPERSET of the backend's
                    requirement (which asks for the field on side / other /
                    unknown) and of the old side-or-diagonal UI, so no template
                    state can ever demand a field the form has hidden.
                    Kept on diagonal: there the facing sign is what tells the
                    coaching narrative which way "forward" is. */}
                {facingSelectorVisible && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-text-muted uppercase tracking-wide">
                      Which way is the performer facing?
                    </label>
                    {/* WHAT / WHY / HOW — docs/COACH_FACING_COPY_STANDARD.md.
                        The old copy named the internal feature ("axis
                        coaching") and told the coach it didn't affect
                        scoring, which stopped being true once near-side and
                        far-side checks were read differently. */}
                    <p className="text-xs text-text-muted leading-relaxed">
                      Pick the way the performer's front points in this video — toward the left
                      of the picture, or the right. It tells us which side of their body is
                      nearer the camera, so each checkpoint is read from the right side. Look at
                      the first frame: if their chest points to the left of the screen, choose
                      "Facing left".
                    </p>
                    <div className="flex gap-2">
                      {(['left', 'right'] as const).map((dir) => (
                        <button
                          key={dir}
                          type="button"
                          /* WRITER 6 (selector). Picking a direction is always
                             a coach decision. Toggling the SELECTED chip off
                             clears both fields together — but only where the
                             field is optional (see facingClearable), so the
                             coach cannot leave the form in a state the save
                             will reject. */
                          onClick={() =>
                            setFacing(facingSelectorClick(facing, dir, facingClearable))
                          }
                          className={`px-3 py-1.5 rounded-sm-control text-sm font-medium border transition-colors ${
                            facingDirection === dir
                              ? 'bg-accent text-text-inverse border-accent'
                              : 'bg-dark-elevated-2 text-text-primary border-border-subtle hover:bg-dark-elevated-1'
                          }`}
                        >
                          {dir === 'left' ? '← Facing left' : 'Facing right →'}
                        </button>
                      ))}
                      {/* WRITER 7 (clear) — offered only where the field is
                          optional, for the same reason as the toggle-off. */}
                      {facingDirection && facingClearable && (
                        <button
                          type="button"
                          onClick={() => setFacing(facingCleared())}
                          className="px-3 py-1.5 rounded-sm-control text-sm font-medium border border-border-subtle bg-dark-elevated-2 text-text-muted hover:bg-dark-elevated-1 transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {!facingClearable && (
                      <p className="text-xs text-text-muted">
                        Needed before you can finalize a template filmed from this angle.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Performance Requirements — shown to athletes before they record */}
              <TagInput
                label="Performance Requirements (optional)"
                tags={performanceNotes}
                onChange={setPerformanceNotes}
                placeholder="e.g., Face sideways toward the camera, Feet in a single line..."
                helpText="Notes for athletes about how to perform the movement when recording. These appear on the evaluation screen before they film."
              />

              <Input
                label="Description (optional)"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Describe this movement template"
              />
              <div className="space-y-2">
                <label htmlFor="template-notes" className="text-sm font-medium text-text-secondary">
                  Notes (optional)
                </label>
                <textarea
                  id="template-notes"
                  value={templateNotes}
                  onChange={(e) => {
                    setTemplateNotes(e.target.value);
                    setNotesToChecksStatus({ type: 'idle' });
                  }}
                  placeholder="What matters most in this movement? e.g., 'Explosive extension matters more than stable base. Watch for the front knee collapsing inward.'"
                  rows={3}
                  className="w-full px-3 py-2 border border-border-subtle rounded-sm-control bg-dark-elevated-2 text-text-primary placeholder:text-text-muted focus:ring-accent focus:border-accent"
                />
                {/* PR-9 (D2's sibling): the box a coach is already looking at
                    can produce proposed checks, through the same extraction a
                    pasted note goes through. The notes themselves are
                    unaffected — they stay here and are saved with the
                    template. */}
                {templateNotes.trim() && isPaidCoach(user) && (
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleTurnNotesIntoChecks}
                      disabled={notesToChecksStatus.type === 'loading'}
                      className="text-xs px-3 py-1.5 rounded-md bg-dark-elevated-2 text-text-secondary hover:text-text-primary border border-border-subtle disabled:opacity-50 transition-colors"
                    >
                      {notesToChecksStatus.type === 'loading'
                        ? 'Reading your notes…'
                        : 'Turn this into checks'}
                    </button>
                    {notesToChecksStatus.message && (
                      <span
                        className={cn(
                          'text-xs',
                          notesToChecksStatus.type === 'success'
                            ? 'text-success'
                            : notesToChecksStatus.type === 'error'
                              ? 'text-warning'
                              : 'text-text-muted',
                        )}
                        role={notesToChecksStatus.type === 'error' ? 'alert' : undefined}
                      >
                        {notesToChecksStatus.message}
                      </span>
                    )}
                  </div>
                )}
                <p className="text-xs text-text-muted">
                  These notes are saved with the template.
                  {isPaidCoach(user)
                    ? ' Use “Turn this into checks” if you also want us to suggest checks from what you wrote.'
                    : ''}
                </p>
              </div>
              <TagInput
                label="Coaching Cues (optional)"
                tags={coachingCues}
                onChange={setCoachingCues}
                placeholder="e.g., drive through the heel, chest up"
                helpText="Short phrases you already use with athletes for this movement"
              />
              <TagInput
                label="Common Mistakes (optional)"
                tags={commonMistakes}
                onChange={setCommonMistakes}
                placeholder="Add custom mistakes or select from suggestions"
                suggestions={COMMON_MISTAKES_BY_MOVEMENT_TYPE[movementType] || []}
                maxTags={8}
                helpText="Known error patterns for this movement type"
              />

              {/* Template Summary */}
              <div className="pt-4 border-t border-border-subtle bg-dark-elevated-1 -mx-6 px-6 -mb-6 pb-6 rounded-b-card">
                <h4 className="font-medium mb-3 text-text-primary">Summary</h4>
                <dl className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-text-secondary">Video:</dt>
                    <dd className="font-medium text-text-primary truncate max-w-[200px]">{videoId}</dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-text-secondary">Facing:</dt>
                    <dd>
                      {facingDirection ? (
                        <FacingDirectionBadge
                          direction={facingDirection}
                          confidence={facingDetection.confidence}
                          isAutoApplied={facingDetection.isAutoApplied}
                        />
                      ) : (
                        <span className="text-text-muted">Not detected</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-text-secondary">Phases:</dt>
                    <dd className="font-medium text-text-primary">
                      {manualPhases.length} defined ({phaseConfigs.filter(c => c.enabled).length} enabled for scoring)
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-text-secondary">Annotations:</dt>
                    <dd className="font-medium text-text-primary">{annotations.length} regions</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-text-secondary">Duration:</dt>
                    <dd className="font-medium text-text-primary">
                      {((trimEndFrame - trimStartFrame + 1) / videoMetadata.fps).toFixed(2)}s
                    </dd>
                  </div>
                </dl>
              </div>

              {error && <Alert variant="error" className="mt-4">{error}</Alert>}

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(3)}>
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveTemplate}
                  disabled={isSaving || !templateName}
                  className="flex-1"
                >
                  {isSaving
                    ? (isEditMode ? 'Saving...' : 'Creating...')
                    : (isEditMode ? 'Save Changes' : 'Create Template')}
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  // Show a loading state while fetching the existing template in edit mode
  if (isEditMode && isLoadingTemplate) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Coaching Studio"
          description="Loading template data..."
          showBack
        />
        <Card>
          <CardContent className="py-12 text-center text-text-muted">
            Loading template...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <PageHeader
          title="Coaching Studio"
          description={
            isEditMode
              ? 'Update this reference movement pattern'
              : 'Define a reference movement pattern for athlete evaluation'
          }
          showBack
        />
        {/* Guide toggle button — visible when guide is dismissed */}
        {!isGuideOpen && (
          <button
            type="button"
            data-testid="studio-guide-button"
            onClick={() => setGuideOpen(true)}
            aria-label="Open coaching guide"
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors mt-1"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--color-surface-raised-3) 80%, transparent)',
              border: '1px solid color-mix(in srgb, var(--color-accent-cyan) 40%, transparent)',
              color: 'var(--color-accent-cyan)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <QuestionMarkCircleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Guide
          </button>
        )}
      </div>

      {/* Adaptive Guide Overlay — B-2026-0058 */}
      <StudioGuideOverlay />

      {/* Step Indicator — clickable in edit mode for free navigation */}
      <StepIndicator
        steps={WIZARD_STEPS}
        currentStep={currentStep}
        onStepClick={isEditMode ? (i) => setCurrentStep(i) : undefined}
      />

      {/* Current step content */}
      {renderStepContent()}

      {/* Facing Direction Confirmation Dialog */}
      {facingDetection.direction && (
        <FacingConfirmationDialog
          isOpen={showFacingConfirmDialog}
          direction={facingDetection.direction}
          confidence={facingDetection.confidence}
          onConfirm={handleFacingConfirm}
          onSwitch={handleFacingSwitch}
          onClose={handleFacingDismiss}
        />
      )}
    </div>
  );
}

export default CreateTemplate;
